// Port of Networking/RetryPolicy.swift: exponential backoff with jitter, and the `Retry-After` parser.
// Durations are seconds (Swift `TimeInterval`).

import type { EpochMs } from '../core/models';
import { isRetryable, retryAfterOf, type NetworkError } from './networkError';

/**
 * Exponential backoff with jitter for retryable network errors. Attempts are 1-based and `maxAttempts` counts the
 * first try: the default policy makes at most 3 requests, waiting ~0.5 s then ~1 s in between.
 */
export interface RetryPolicy {
  /** Total number of attempts including the first one (values < 1 behave as 1). */
  maxAttempts: number;
  /** Delay before the second attempt, seconds; doubles for every further attempt. */
  baseDelay: number;
  /** Upper bound for the computed backoff, and the longest `Retry-After` the client waits in-line (seconds). */
  maxDelay: number;
  /** Relative jitter (0…1): the delay is spread uniformly over ±`jitterFraction` of the backoff. */
  jitterFraction: number;
  /** Whether `429 Too Many Requests` is retried in-line. api.met.no's terms say a throttled client must slow down. */
  retriesThrottledRequests: boolean;
}

export function makeRetryPolicy(
  maxAttempts: number,
  baseDelay: number,
  maxDelay: number,
  jitterFraction = 0,
  retriesThrottledRequests = true,
): RetryPolicy {
  return { maxAttempts, baseDelay, maxDelay, jitterFraction, retriesThrottledRequests };
}

function sanitized(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

export const RetryPolicies = {
  /** 3 attempts, 0.5 s base delay, 8 s cap, ±20 % jitter. */
  default: makeRetryPolicy(3, 0.5, 8, 0.2),
  /** A single attempt, never retried. */
  none: makeRetryPolicy(1, 0, 0, 0),
  /** Like `default`, but a `429` is never repeated in-line. */
  noThrottledRetry: makeRetryPolicy(3, 0.5, 8, 0.2, false),
} as const;

/** `maxAttempts` clamped to at least 1. */
export function effectiveMaxAttempts(policy: RetryPolicy): number {
  return Number.isFinite(policy.maxAttempts) ? Math.max(1, Math.trunc(policy.maxAttempts)) : 1;
}

/** Exponential backoff for `attempt` (1-based), capped at `maxDelay`, without jitter. */
export function backoffWithoutJitter(policy: RetryPolicy, attempt: number): number {
  const base = sanitized(policy.baseDelay);
  const cap = sanitized(policy.maxDelay);
  // Bound the exponent so `base * 2^n` stays finite (and 0 * Infinity never produces NaN).
  const exponent = Math.min(Math.floor(Number.isNaN(attempt) ? 1 : Math.max(attempt, 1)) - 1, 60);
  return Math.min(cap, base * 2 ** exponent);
}

/**
 * Delay (seconds) to wait after failed attempt number `attempt` before the next one. `jitterUnit` in -1…1 picks the
 * position in the jitter band (-1 shortest, 0 none, +1 longest); omitted, it is random. A server `Retry-After` longer
 * than the backoff wins.
 */
export function retryDelay(policy: RetryPolicy, attempt: number, retryAfter?: number, jitterUnit?: number): number {
  const unitInput = jitterUnit ?? Math.random() * 2 - 1;
  const cap = sanitized(policy.maxDelay);
  const backoff = backoffWithoutJitter(policy, attempt);
  const jitter = Math.min(1, Math.max(0, Number.isFinite(policy.jitterFraction) ? policy.jitterFraction : 0));
  const unit = Math.min(1, Math.max(-1, Number.isFinite(unitInput) ? unitInput : 0));
  const jittered = Math.min(cap, Math.max(0, backoff * (1 + jitter * unit)));
  if (retryAfter !== undefined && Number.isFinite(retryAfter)) return Math.max(jittered, Math.max(0, retryAfter));
  return jittered;
}

/**
 * Whether a request that failed with `error` on attempt `attempt` should be attempted again: the error is retryable,
 * attempts remain, and any `Retry-After` fits within `maxDelay`. A longer `Retry-After` is not waited for in-line.
 */
export function shouldRetry(policy: RetryPolicy, error: NetworkError, attempt: number): boolean {
  if (!isRetryable(error) || !(attempt < effectiveMaxAttempts(policy))) return false;
  if (!policy.retriesThrottledRequests && error.kind === 'http' && error.status === 429) return false;
  const retryAfter = retryAfterOf(error);
  if (retryAfter !== undefined && Number.isFinite(retryAfter) && retryAfter > sanitized(policy.maxDelay)) return false;
  return true;
}

// MARK: Retry-After

const monthIndex: Record<string, number> = {
  Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
};
const shortDays = new Set(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']);
const longDays = new Set(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']);

const imfFixdate = /^([A-Za-z]{3}), (\d{2}) ([A-Za-z]{3}) (\d{4}) (\d{2}):(\d{2}):(\d{2}) (GMT|UTC)$/;
const rfc850 = /^([A-Za-z]+), (\d{2})-([A-Za-z]{3})-(\d{2}) (\d{2}):(\d{2}):(\d{2}) (GMT|UTC)$/;
const asctime = /^([A-Za-z]{3}) ([A-Za-z]{3}) (\d{1,2}) (\d{2}):(\d{2}):(\d{2}) (\d{4})$/;

function utc(year: number, month: string, day: number, h: number, m: number, s: number): EpochMs | undefined {
  const mo = monthIndex[month];
  if (mo === undefined || h > 23 || m > 59 || s > 60) return undefined;
  const date = new Date(Date.UTC(year, mo, day, h, m, s));
  if (date.getUTCMonth() !== mo || date.getUTCDate() !== day) return undefined; // strict: no 31 Feb
  return date.getTime();
}

/** Parser for the HTTP `Retry-After` header (RFC 9110 §10.2.3): delay-seconds or an HTTP-date. */
export const RetryAfter = {
  /**
   * Seconds to wait according to a `Retry-After` value, or undefined when malformed. HTTP-dates are measured from
   * `now` (prefer the response's `Date` header to avoid clock skew); dates in the past yield 0.
   */
  interval(value: string, now: EpochMs): number | undefined {
    const trimmed = value.trim();
    if (trimmed === '') return undefined;
    if (/^[0-9.]+$/.test(trimmed)) {
      const seconds = Number(trimmed);
      return Number.isFinite(seconds) ? Math.max(0, seconds) : undefined;
    }
    const date = RetryAfter.parseHTTPDate(trimmed);
    if (date === undefined) return undefined;
    return Math.max(0, (date - now) / 1000);
  },

  /**
   * Parses an HTTP-date in IMF-fixdate (`Sun, 06 Nov 1994 08:49:37 GMT`), obsolete RFC 850
   * (`Sunday, 06-Nov-94 08:49:37 GMT`, two-digit years in 1970…2069) or asctime (`Sun Nov  6 08:49:37 1994`) format.
   */
  parseHTTPDate(value: string): EpochMs | undefined {
    const collapsed = value.trim().split(/\s+/).join(' ');
    if (collapsed === '') return undefined;
    let m = imfFixdate.exec(collapsed);
    if (m) return shortDays.has(m[1]) ? utc(+m[4], m[3], +m[2], +m[5], +m[6], +m[7]) : undefined;
    m = rfc850.exec(collapsed);
    if (m) {
      if (!longDays.has(m[1])) return undefined;
      const yy = +m[4];
      return utc(yy < 70 ? 2000 + yy : 1900 + yy, m[3], +m[2], +m[5], +m[6], +m[7]);
    }
    m = asctime.exec(collapsed);
    if (m) return shortDays.has(m[1]) ? utc(+m[7], m[2], +m[3], +m[4], +m[5], +m[6]) : undefined;
    return undefined;
  },

  /** Retry-After interval from raw header values, with the `Date` header as reference clock when present. */
  fromHeaders(
    retryAfterHeader: string | undefined,
    dateHeader: string | undefined,
    fallbackNow: () => EpochMs,
  ): number | undefined {
    if (retryAfterHeader === undefined) return undefined;
    const now = (dateHeader !== undefined ? RetryAfter.parseHTTPDate(dateHeader) : undefined) ?? fallbackNow();
    return RetryAfter.interval(retryAfterHeader, now);
  },
};
